import { FC, LazyExoticComponent } from 'react';

import { IContentsGate, ICreateSendUnsetMskuState } from './createSendContents';
import { IEmailTokenGate } from './createSendEmail';
import { TCreateSendLinkActionTarget } from './createSendLinkAction';
import { ILinkSettingsGate } from './createSendLinkSettings';
import { IRecipientsGate, TAddressMode } from './createSendRecipients';
import { IShipmentInfoGate } from './createSendShipmentInfo';

export interface IStepNavigation {
  currentIndex: number;
  prev?: string;
  next?: string;
  backLabel?: string;
  continueToLabel?: string;
}

export enum CreateSendMethodEnum {
  Recipients = 'recipients',
  Link = 'link',
  Location = 'location',
}

/** Kept as the union so the many existing annotations and stored values stay valid. */
export type TCreateSendMethod = `${CreateSendMethodEnum}`;

export enum CreateSendRecipientsModeEnum {
  One = 'one',
  Many = 'many',
}

/** Same reasoning as `TCreateSendMethod`: the literal union keeps existing call sites valid. */
export type TCreateSendRecipientsMode = `${CreateSendRecipientsModeEnum}`;

export enum CreateSendContentTypeEnum {
  Single = 'single',
  Pyg = 'pyg',
  Custom = 'custom',
}

export type TCreateSendContentType = CreateSendContentTypeEnum;

export type TCreateSendStep =
  | 'contents'
  | 'recipients'
  | 'email'
  | 'review'
  | 'link-settings'
  | 'shipment-info'
  | 'claim-page'
  | 'custom-message';

export interface ICreateSendValidity {
  email: boolean;
  review: boolean;
  linkSettings: boolean;
  claimPage: boolean;
  customMessage: boolean;
}

export interface ICreateSendStepContext {
  method: TCreateSendMethod | null;
  recipientsMode: TCreateSendRecipientsMode | null;
  contentType: TCreateSendContentType | null;
  addressMode: TAddressMode | null;
  isClaimBased: boolean;
  isContentDigital: boolean;
  isLinkPayloadDigital: boolean;
  isNotecardDisabled: boolean;
  emailNotificationsOn: boolean;
  isEmailPreparing: boolean;
  unsetMsku: ICreateSendUnsetMskuState;
  emailToken: IEmailTokenGate;
  validity: ICreateSendValidity;
  contents: IContentsGate;
  recipients: IRecipientsGate;
  shipmentInfo: IShipmentInfoGate;
  linkSettings: ILinkSettingsGate;
}

export type TFooterNoteIcon = 'mail-off' | 'warning';

export type TStepBlockingTone = 'info' | 'error';

export interface IStepContinueGuard {
  key: string;
}

export interface IStepFooterNote {
  text: string;
  icon: TFooterNoteIcon;
}

export interface IStepBlockingAction {
  label: string;
  target: TCreateSendLinkActionTarget;
}

export interface ICreateSendStepProps {
  stepId: string;
  stepSlug?: TCreateSendStep;
  prev?: string;
  next?: string;
  backLabel?: string;
  continueToLabel?: string;
  stepNumber: number;
  stepCount: number;
  isValid: boolean;
  blockingMessage?: string;
  blockingAction?: IStepBlockingAction;
  blockingTone?: TStepBlockingTone;
  footerNote?: IStepFooterNote;
  ctaLabel?: string;
  softBlock?: boolean;
  continueGuard?: IStepContinueGuard;
  isCustomSend: boolean;
}

export interface ICreateSendStepDefinition {
  slug: TCreateSendStep;
  routeSlug: string;
  navLabel: string;
  backLabel?: string;
  isApplicable: (ctx: ICreateSendStepContext) => boolean;
  validate: (ctx: ICreateSendStepContext) => boolean;
  blockingMessage?: string | ((ctx: ICreateSendStepContext) => string | undefined);
  blockingAction?: (ctx: ICreateSendStepContext) => IStepBlockingAction | undefined;
  blockingTone?: (ctx: ICreateSendStepContext) => TStepBlockingTone | undefined;
  footerNote?: IStepFooterNote | ((ctx: ICreateSendStepContext) => IStepFooterNote | undefined);
  ctaLabel?: (ctx: ICreateSendStepContext) => string | undefined;
  softBlock?: (ctx: ICreateSendStepContext) => boolean;
  continueGuard?: (ctx: ICreateSendStepContext) => IStepContinueGuard | undefined;
  prevOverride?: string;
  component: LazyExoticComponent<FC<ICreateSendStepProps>>;
}

export interface ICreateSendStep {
  slug: TCreateSendStep;
  routeSlug: string;
  navLabel: string;
  backLabel?: string;
  valid: boolean;
  blockingMessage?: string;
  blockingAction?: IStepBlockingAction;
  blockingTone?: TStepBlockingTone;
  footerNote?: IStepFooterNote;
  ctaLabel?: string;
  softBlock?: boolean;
  continueGuard?: IStepContinueGuard;
  prevOverride?: string;
}
